import type {
	PrescriptionSelectionCourse,
	PrescriptionSelectionFocusAreaSelector,
} from '@/types/fetchPrescriptionSelection'
import {
	CourseSelections,
	ElectiveSelectionState,
	EMPTY_FOCUS_AREA_GUID,
	expandSelectorsByQty,
	getCourseOptionGroups,
	isElectiveCourse,
	isStudiesVariant,
	resolveCourseWithElective,
	resolveFocusAreaId,
	SelectionState,
} from './prescriptionSelectionRules'

export interface PrescriptionPrintFocusArea {
	'focus-areas-cmsid': string
	'prescription-option-cmsid'?: string
	'prescription-work-cmsid'?: string[]
	'prescription-bundle-work-cmsid'?: string[]
	'prescription-study-cmsid'?: string[]
}

export interface PrescriptionPrintCourse {
	'course-cmsid': string
	'focus-areas': PrescriptionPrintFocusArea[]
}

export interface PrescriptionPrintPayload {
	pdf: boolean
	listcodename: string
	courses: PrescriptionPrintCourse[]
}

interface FocusAreaAccumulator {
	selector: PrescriptionSelectionFocusAreaSelector | undefined
	plainWorkIds: string[]
	bundleWorkIdGroups: string[][]
}

const isCourseSelectionsEmpty = (
	selections: CourseSelections | undefined,
): boolean => {
	if (!selections) return true
	return !Object.values(selections).some(
		(slot) => slot && slot.workIds.length > 0,
	)
}

const buildCourseEntry = (
	course: PrescriptionSelectionCourse,
	courseSelections: CourseSelections,
	electiveOptionIndex: number | undefined,
): PrescriptionPrintCourse | null => {
	const isElective = isElectiveCourse(course)
	const isStudies = isStudiesVariant(course)
	const hasOption = isElective || isStudies

	const resolvedCourse = isElective
		? resolveCourseWithElective(course, electiveOptionIndex)
		: course

	const resolveOptionCmsid = (optionGroupIndex: number): string =>
		getCourseOptionGroups(course)[optionGroupIndex]?.[
			'option-cmsid'
		]?.trim() || EMPTY_FOCUS_AREA_GUID

	if (isStudies) {
		const studyIds: string[] = []
		Object.entries(courseSelections)
			.map(
				([slotIdxRaw, slot]) =>
					[Number(slotIdxRaw), slot] as [
						number,
						CourseSelections[number] | undefined,
					],
			)
			.sort((a, b) => a[0] - b[0])
			.forEach(([, slot]) => {
				if (!slot) return
				slot.workIds.forEach((id) => studyIds.push(id))
			})
		if (studyIds.length === 0) return null
		const focusAreaEntry: PrescriptionPrintFocusArea = {
			'focus-areas-cmsid': resolveFocusAreaId(course.selectors?.[0]),
			'prescription-study-cmsid': studyIds,
		}
		if (hasOption) {
			focusAreaEntry['prescription-option-cmsid'] = resolveOptionCmsid(0)
		}
		return {
			'course-cmsid': course['course-cmsid'] || EMPTY_FOCUS_AREA_GUID,
			'focus-areas': [focusAreaEntry],
		}
	}

	const electiveOptionCmsid =
		isElective && electiveOptionIndex !== undefined
			? resolveOptionCmsid(electiveOptionIndex)
			: EMPTY_FOCUS_AREA_GUID

	const expanded = expandSelectorsByQty(resolvedCourse)
	const groups = new Map<string, FocusAreaAccumulator>()
	const focusAreaOrder: string[] = []

	for (const slot of expanded) {
		const selection = courseSelections[slot.flatSlotIndex]
		if (!selection || selection.workIds.length === 0) continue

		const focusAreaId = resolveFocusAreaId(slot.selector)
		if (!groups.has(focusAreaId)) {
			groups.set(focusAreaId, {
				selector: slot.selector,
				plainWorkIds: [],
				bundleWorkIdGroups: [],
			})
			focusAreaOrder.push(focusAreaId)
		}
		const acc = groups.get(focusAreaId)!

		if (selection.workIds.length === 1) {
			acc.plainWorkIds.push(selection.workIds[0])
		} else {
			acc.bundleWorkIdGroups.push([...selection.workIds])
		}
	}

	const focusAreas: PrescriptionPrintFocusArea[] = []
	for (const focusAreaId of focusAreaOrder) {
		const acc = groups.get(focusAreaId)!
		if (acc.plainWorkIds.length > 0) {
			const entry: PrescriptionPrintFocusArea = {
				'focus-areas-cmsid': focusAreaId,
				'prescription-work-cmsid': acc.plainWorkIds,
			}
			if (hasOption) {
				entry['prescription-option-cmsid'] = electiveOptionCmsid
			}
			focusAreas.push(entry)
		}
		for (const bundleIds of acc.bundleWorkIdGroups) {
			const entry: PrescriptionPrintFocusArea = {
				'focus-areas-cmsid': focusAreaId,
				'prescription-bundle-work-cmsid': bundleIds,
			}
			if (hasOption) {
				entry['prescription-option-cmsid'] = electiveOptionCmsid
			}
			focusAreas.push(entry)
		}
	}

	if (focusAreas.length === 0) return null

	return {
		'course-cmsid': course['course-cmsid'] || EMPTY_FOCUS_AREA_GUID,
		'focus-areas': focusAreas,
	}
}

export const buildPrescriptionPrintPayload = (
	courses: PrescriptionSelectionCourse[],
	selectionState: SelectionState,
	electiveState: ElectiveSelectionState,
	listcodename: string,
	pdf: boolean = false,
): PrescriptionPrintPayload => {
	const payloadCourses: PrescriptionPrintCourse[] = []

	for (const course of courses) {
		const title = course['course-title']
		const courseSelections = selectionState[title]
		if (isCourseSelectionsEmpty(courseSelections)) continue
		const entry = buildCourseEntry(
			course,
			courseSelections!,
			electiveState[title],
		)
		if (entry) payloadCourses.push(entry)
	}

	return {
		pdf,
		listcodename,
		courses: payloadCourses,
	}
}
